import { CSSProperties } from 'react';

const defaultMaxInputLength: number = 32;
const defaultMaxEmailLength: number = 128;
const maxW2SystemNameLength: number = 12;

const validateInputChars = (str: string): boolean => {
	const invalidCharacters: string[] = [
		'<',
		'>',
		'&lt;',
		'&gt;',
		'&#60;',
		'&#62;',
		'/',
		'&#47;',
		'\\',
		'&#92;',
		'&',
		'&amp;',
		'&#38;',
		';',
	];
	const containesChar = (char: string): boolean => str.includes(char);
	const isIncorrectValue = invalidCharacters.some(containesChar);
	if (isIncorrectValue) {
		return true;
	}
	if (!str.trim()) {
		return true;
	}
	return false;
};

const validateEmail = (email: string): boolean => {
	//RFC 2821 standard for email validation:
	const emailRegex: RegExp = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
	if (!email.match(emailRegex)) {
		return true;
	}
	return false;
};

const validatePhone = (phone: string): boolean => {
	const phoneRex = /^\+\d{9,16}$/;
	//hide error if regex doesn't return null:
	if (!phone.match(phoneRex)) {
		return true;
	}
	return false;
};

const validateTime = (time: string): boolean => {
	const timeRegex = /^[0-2][0-9]:[0-5][0-9]$/;
	const isTimeRegexIncorrect = !timeRegex.test(time);
	const hour = time.substr(0, 2);

	if (isTimeRegexIncorrect || hour > '23') {
		return true;
	}
	return false;
};

const validateShortDate = (date: string): boolean => {
	const winterSeasonRegex = /^[0-3][0-9]\.[0-1][0-9]$/;
	const day = date.substr(0, 2);
	const month = date.substr(3, 2);
	const isIncorrectDatePattern = !winterSeasonRegex.test(date);
	const isIncorrectMonthOrDay = month > '12' || day > '31';
	const shorterMonths: string[] = ['04', '06', '09', '11'];
	const isMonthShorter = shorterMonths.some((shortMonth) => month === shortMonth);
	const isFebruary = month === '02';

	if (isIncorrectDatePattern || isIncorrectMonthOrDay || (isMonthShorter && day > '30') || (isFebruary && day > '28')) {
		return true;
	}
	return false;
};

const validateSimpleInt = (simpleInt: string): boolean => {
	const simpleIntRegex = /^[0-9]+$/;
	const isSimpleIntPatternIncorrect = !simpleIntRegex.test(simpleInt);
	if (isSimpleIntPatternIncorrect) {
		return true;
	}
	return false;
};

const validateAngle = (angle: string): boolean => {
	const angleRegex = /^[0-9]\.[0-9][0-9]$/;
	const isAngleIncorrect = !angleRegex.test(angle);
	if (isAngleIncorrect) {
		return true;
	}
	return false;
};

const validateLatitude = (latitude: string): boolean => {
	const latitudeRegex = /^-{0,1}[1-9]{0,1}[0-9]{0,1}\.{0,1}[0-9]{0,10}$/;
	const isLatitudePatternIncorrect = !latitudeRegex.test(latitude);
	const isLatitudeIncorrect = parseFloat(latitude) > 90 || parseFloat(latitude) < -90;
	if (isLatitudePatternIncorrect || isLatitudeIncorrect) {
		return true;
	}
	return false;
};

const validateLongitude = (longitude: string): boolean => {
	const longitudeRegex = /^-{0,1}[1-9]{0,1}[0-9]{0,2}\.{0,1}[0-9]{0,10}$/;
	const isLongitudePatternIncorrect = !longitudeRegex.test(longitude);
	const isLongitudeIncorrect = parseFloat(longitude) > 180 || parseFloat(longitude) < -180;
	if (isLongitudePatternIncorrect || isLongitudeIncorrect) {
		return true;
	}
	return false;
};

const validatePostalCode = (postalCode: string): boolean => {
    return postalCode.length && postalCode.length > 16;
};

const validateBuildingNumber = (buildingNumber: string): boolean => {
    return buildingNumber.length && buildingNumber.length > 8;
};

const validateNullableString = (nullableString: string): boolean => {
    return nullableString.length && nullableString.length > 64;

};

const validateMeasurementPeriod = (measurementPeriod: string): boolean => {
	if (!measurementPeriod.length) {
        return false;
    }
	const measurementPeriodRegex = /^([0-9][0-9]|2[0-3]):[0-9][0-9]$/;
	const measurementPeriodHours = parseInt(measurementPeriod.substring(0,2));
	const measurementPeriodMinutes = parseInt(measurementPeriod.substring(3,5));
	return !measurementPeriod.match(measurementPeriodRegex) || 
			measurementPeriodHours * 60 + measurementPeriodMinutes < 15 ||
			measurementPeriodHours * 60 + measurementPeriodMinutes > 720;
	};

const validateCommunicationPeriod = (communicationPeriod: string): boolean => {
	if (!communicationPeriod.length) {
		return true;
	}
	const communicationPeriodRegex = /^([0-9][0-9]|2[0-3]):[0-9][0-9]$/;
	const communicationPeriodHours = parseInt(communicationPeriod.substring(0,2));
	const communicationPeriodMinutes = parseInt(communicationPeriod.substring(3,5));
	return !communicationPeriod.match(communicationPeriodRegex) || 
		communicationPeriodHours * 60 + communicationPeriodMinutes < 1 || 
		communicationPeriodHours * 60 + communicationPeriodMinutes > 15;
	};

const validateInclinometerRange = (inclinometerRange: string): boolean => {
	const rangeRegex = /^[0-9](\.[0-9]){0,1}$/;
	return !!inclinometerRange && (!rangeRegex.test(inclinometerRange) || !parseFloat(inclinometerRange));
};

const validateAngleChangeThreshold = (angleChangeThreshold: string): boolean => {
    const regex = /^[0-9]+(\.[0-9]{1,3})?$/;
    const value = parseFloat(angleChangeThreshold);
    return (
        !angleChangeThreshold ||
        !regex.test(angleChangeThreshold) ||
        isNaN(value) ||
        value < 0 ||
        value > 65.535
    );
};

const validateDistanceChangeThreshold = (distanceChangeThreshold: string): boolean => {
    const regex = /^[0-9]+$/;
    const value = parseFloat(distanceChangeThreshold);
    return (
        !distanceChangeThreshold ||
        !regex.test(distanceChangeThreshold) ||
        isNaN(value) ||
        value < 0 ||
        value > 255
    );
};

const validatePort = (port: string): boolean => {
	if (!port) {
		return false;
	}
	const portRegex = /^[0-9]+$/;
	const value = parseInt(port, 10);
	return !portRegex.test(port) || isNaN(value) || value < 1 || value > 65535;
};

export const getHeaderStyle = (errorState: boolean): CSSProperties => {
	return {
		backgroundColor: errorState ? '#f44336' : '#5cb860',
		color: '#fff',
		fontWeight: 400,
		textAlign: 'center',
		padding: '5px 10px',
		margin: '15px 0 0 0',
		borderRadius: '5px',
	};
};

export const headerStyleNoMeasurements: CSSProperties = {
	backgroundColor: '#ffa21a',
	color: '#fff',
	fontWeight: 400,
	textAlign: 'center',
	padding: '5px 10px',
	margin: '10px 0 0 0',
	borderRadius: '5px',
	fontSize: '16px',
	opacity: '1',
};

export const sweetAlertHeaderStyle: CSSProperties = {
	color: 'black',
	textAlign: 'left',
	transform: 'translateY(-15px)',
};

const headerStyle: CSSProperties = {
	color: '#fff',
	fontWeight: 400,
	textAlign: 'center',
	padding: '5px 0',
	margin: '15px 0 0 0',
	borderRadius: '5px',
};
export const headerStyleFail: CSSProperties = { ...headerStyle, backgroundColor: '#f44336' };
export const headerStyleSuccess: CSSProperties = { ...headerStyle, backgroundColor: '#5cb860' };
export const emptyHeaderStyle = { margin: '24px 0 0 0', height: '20px' };
export const contentCenteredFlex = { display: 'flex', justifyContent: 'center', alignItems: 'center' };

const parseDateFromDb = (dateFromDb: string): Date => {
	//db format: "dd.mm"
	const splittedDate = dateFromDb.split('.');
	const transformedDate = new Date();
	transformedDate.setMonth(+splittedDate[1] -1);
	transformedDate.setDate(+splittedDate[0]);
	return transformedDate;
};

const parseDateFromPickerToDb = (dateFromPicker: Date): string => {
	const month = dateFromPicker.getMonth() < 10 ? `0${dateFromPicker.getMonth() + 1}` : dateFromPicker.getMonth() + 1;
	const day = dateFromPicker.getDate() < 10 ? `0${dateFromPicker.getDate()}` : dateFromPicker.getDate();
	return `${day}.${month}`;
};

const calculateRainstormModeDate = (currentRainstormModeEnd: string, selectedValue: string): string => {
	if (selectedValue === '0') {
		return currentRainstormModeEnd;
	}
	const currentValue = Date.now() + parseInt(selectedValue);
	const currentDate = new Date(currentValue);
	// getTimezoneOffset return negative value if timezone has plus sign:
	const offsetInMilliseconds = -currentDate.getTimezoneOffset() * 60 * 1000;
	// replace T sign with a space and remove Z sign from ISOString date:
	const parsedDate = new Date(currentValue + offsetInMilliseconds).toISOString().replace('T', ' ').replace('Z', '');
	return parsedDate;
};

export const colorMap = {
    unknown_or_NA: 'L0',
    none: 'L0',
    L1: 'L1',
    L2: 'L2',
    L3: 'L3',
    L4: 'L4',
};

export const batteryColorMap = {
	battery_empty: 'red',
	battery_weak: 'yellow',
	battery_good_normal: 'green',
	line: 'green'
};


const utils = {
	calculateRainstormModeDate,
	defaultMaxInputLength,
	defaultMaxEmailLength,
	emptyHeaderStyle,
	getHeaderStyle,
	validateInputChars,
	validateEmail,
	validatePhone,
	validateTime,
	validateShortDate,
	validateSimpleInt,
	validateAngle,
	maxW2SystemNameLength,
	sweetAlertHeaderStyle,
	parseDateFromDb,
	parseDateFromPickerToDb,
	validateLatitude,
	validateLongitude,
	validatePostalCode,
	validateBuildingNumber,
	validateNullableString,
	validateMeasurementPeriod,
	validateInclinometerRange,
	validateCommunicationPeriod,
	validateAngleChangeThreshold,
	validateDistanceChangeThreshold,
	validatePort,
	batteryColorMap
};

export default utils;
